import { html, nothing, render } from 'lit-html';
import type { Delta } from 'quill/core';

import { on } from '@disy/lib/event-util';

import { createEditButton } from 'ui/edit-button/edit-button';

import type { DescriptionSubmitCallback } from 'cadenza/ui/description-editing/description-edit-dialog';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';

import './description-editing.css';

const COMPONENT_NAME = 'd-description-editing';

export class DescriptionEditing extends HTMLElement {
  readonly #label;
  readonly #submitCallback;
  #description?: Delta;
  readonly #dataViewId;
  readonly #applyLabel;
  readonly #readOnly;
  readonly #emptyLabel;

  constructor({
    label,
    submitCallback,
    dataViewId,
    applyLabel,
    readOnly,
    emptyLabel,
  }: {
    label?: string;
    submitCallback?: DescriptionSubmitCallback;
    dataViewId?: DataViewId;
    applyLabel?: string;
    readOnly?: boolean;
    emptyLabel?: string;
  }) {
    super();
    this.#label = label;
    this.#submitCallback = submitCallback;
    this.#dataViewId = dataViewId;
    this.#applyLabel = applyLabel;
    this.#readOnly = readOnly;
    this.#emptyLabel = emptyLabel;
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.#render();
  }

  get description() {
    return this.#description;
  }

  set description(value: Delta | undefined) {
    this.#description = value;
    this.#render();
  }

  // eslint-disable-next-line no-restricted-syntax
  async #render() {
    const label = this.#label ? html`<label>${this.#label}</label>` : nothing;
    const editor = await this.#createDescriptionEditor();
    const editButton = this.#createEditButton();

    render(
      html`
        <form-group>
          ${label}
          <div class="d-stack-h space-1">${editor} ${editButton}</div>
        </form-group>
      `,
      this,
    );
  }

  async #createDescriptionEditor() {
    if (!this.#description) {
      return this.#emptyLabel;
    }

    const { DescriptionEditor } = await import('cadenza/ui/description-editing/description-editor');
    return new DescriptionEditor({
      description: this.#description,
      dataViewId: this.#dataViewId,
      readOnly: true,
    });
  }

  #createEditButton() {
    if (!this.#readOnly) {
      const editButton = createEditButton();
      on(editButton, 'click', async () => await this.#openDescriptionEditDialog());
      return editButton;
    }
  }

  async #openDescriptionEditDialog() {
    const { DescriptionEditDialog } = await import('cadenza/ui/description-editing/description-edit-dialog');
    return new DescriptionEditDialog({
      label: this.#label,
      submitCallback: this.#submitCallback,
      description: this.#description,
      applyLabel: this.#applyLabel,
    }).connectAndShow();
  }
}

customElements.define(COMPONENT_NAME, DescriptionEditing);
